/**
 * hkParser — reads the Clock PMS+ "Housekeeping" report exported to Excel and
 * turns it into one record per room for a given day (Oct 2026).
 *
 * Input: rows as arrays (SheetJS sheet_to_json(ws, { header: 1, defval: null })).
 * Pure function, no I/O, so it runs the same in the browser and in tests.
 *
 * Export layout (confirmed on the 25 + 26 Oct 2026 files):
 *   A "203 / Twin | old corridorОК"   (ОК is CYRILLIC — match both scripts)
 *   B "Clean " | "Dirty Checkout discrepancy " | "Dirty Room changed to: 215 "
 *   C Departures "2 + 0"
 *   D In Hotel   "2 + 0 (25 Oct 2026, Night 2 / 4)"
 *   E Expected   "2 + 0 (15:00:00 h.)"
 *   comment rows: merged A:CV, "comment<text> : Booking #…, <guest name>, …"
 *     — only ever for the IN-HOUSE booking. Text after " : Booking" holds the
 *     guest name and is dropped here so no personal data is stored.
 *   footer: "Rooms:" / "Guests:" totals (departures, in hotel, expected).
 */

const PREMIUM_RE = /honeymoon|champagne|deluxe/i;

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
/** "26 Oct 2026" → "2026-10-26" (no Date objects, no timezone). */
export function dateKey(txt) {
  const m = String(txt || '').match(/(\d{1,2})\s+([A-Za-z]{3})[a-z]*\s+(\d{4})/);
  if (!m || !MONTHS[m[2].toLowerCase()]) return null;
  return `${m[3]}-${String(MONTHS[m[2].toLowerCase()]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}

const s = (v) => (v === null || v === undefined ? '' : String(v)).trim();

function pax(txt) {
  const m = s(txt).match(/(\d+)\s*\+\s*(\d+)/);
  return m ? { adults: +m[1], children: +m[2] } : null;
}

function to24(h, m, ap) {
  let hh = parseInt(h, 10);
  const mm = m ? parseInt(m, 10) : 0;
  if (ap) {
    ap = ap.toLowerCase();
    if (ap === 'pm' && hh < 12) hh += 12;
    if (ap === 'am' && hh === 12) hh = 0;
  }
  if (!(hh >= 0 && hh < 24 && mm >= 0 && mm < 60)) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Extras + late checkout + early arrival from a free-text booking comment. */
export function readComment(text) {
  const t = s(text);
  const out = { extras: [], lateCheckout: null, early: false };
  const robes = t.match(/(\d+)\s*(?:x\s*)?(?:bath\s*)?robes?/i);
  if (robes) out.extras.push(`${robes[1]} robes`);
  else if (/robes?\b/i.test(t)) out.extras.push('Robes');
  if (/choc/i.test(t)) out.extras.push('Chocolates');
  if (/flower|bouquet/i.test(t)) out.extras.push('Flowers');
  if (/balloon/i.test(t)) out.extras.push('Balloons');
  if (/prosecco|champagne|bubbly/i.test(t)) out.extras.push('Prosecco');
  if (/split\s*beds?|twin\s*beds/i.test(t)) out.extras.push('Split beds');
  if (/\bcot\b/i.test(t)) out.extras.push('Cot');
  const late = t.match(/late\s*check-?\s*out[^0-9]{0,20}(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?/i);
  if (late) out.lateCheckout = to24(late[1], late[2], late[3] || (parseInt(late[1], 10) < 8 ? 'pm' : null));
  if (/early|as soon as possible|asap/i.test(t)) out.early = true;
  return out;
}

function parseRoomCell(a) {
  // "203 / Twin | old corridorОК"  /  "301 / Lodge Family Room | lodgesOut of service (OOS)  ;"
  const m = s(a).match(/^(\d+)\s*\/\s*(.+?)\s*\|\s*(.*)$/);
  if (!m) return null;
  let rest = m[3];
  const oos = /out of service|\(OOS\)/i.test(rest);
  rest = rest.replace(/out of service.*$/i, '').replace(/[ОOоo][КKкk]\s*$/, '').trim();
  return { room: m[1], type: m[2].trim(), sector: rest, oos };
}

function sectorKey(sector) {
  const x = sector.toLowerCase();
  if (x.includes('lodge')) return 'lodges';
  if (x.includes('new')) return 'new';
  if (x.includes('old')) return 'old';
  return x.replace(/[^a-z0-9]+/g, '-') || 'other';
}

function deriveJob(r) {
  if (r.oos) return 'oos';
  if (r.departing && r.expected) return 'turn';
  if (r.expected) return 'arr';
  if (r.departing) return 'out';
  // Checked in today (Clock moves a guest from Expected to In Hotel on
  // check-in): the room was already made up, so nothing to clean.
  if (r.inHouse && r.inHouse.arrivedKey && r.inHouse.arrivedKey === r.day && r.inHouse.night === 1) return 'in';
  if (r.inHouse) return 'stay';
  if (r.clockStatus === 'dirty') return 'dirty';
  return 'vacant';
}

/**
 * @returns {{ rooms: object[], totals: object|null, check: {ok:boolean, messages:string[]} }}
 */
export function parseHkReport(rows, day = null) {
  const rooms = [];
  let totals = null;
  let current = null;
  let header = false;

  for (const row of rows || []) {
    if (!row || !row.length) continue;
    const a = s(row[0]);
    if (!a) continue;
    if (/^room\s*\|\s*sector/i.test(a)) { header = true; continue; }

    if (/^comment/i.test(a)) {
      if (!current) continue;
      const text = a.replace(/^comment/i, '').split(/\s:\s*Booking\s*#/i)[0].trim();
      if (text) current.comments.push(text);
      continue;
    }
    if (/^rooms:/i.test(a)) {
      totals = totals || {};
      totals.rooms = { departures: +s(row[2]) || 0, inHotel: +s(row[3]) || 0, expected: +s(row[4]) || 0 }; // A:B merged
      continue;
    }
    if (/^guests:/i.test(a)) {
      totals = totals || {};
      totals.guests = { departures: pax(row[2]), inHotel: pax(row[3]), expected: pax(row[4]) };
      continue;
    }

    const rc = parseRoomCell(a);
    if (!rc) continue;
    const b = s(row[1]);
    const eTxt = s(row[4]);
    const dTxt = s(row[3]);
    const inM = dTxt.match(/\((\d{1,2} \w{3} \d{4}),\s*Night\s*(\d+)\s*\/\s*(\d+)\)/i);
    const eta = eTxt.match(/\((\d{1,2}):(\d{2})(?::\d{2})?\s*h\.?\)/i);
    current = {
      ...rc,
      sectorKey: sectorKey(rc.sector),
      premium: PREMIUM_RE.test(rc.type),
      clockStatus: /^dirty/i.test(b) ? 'dirty' : (/^clean/i.test(b) ? 'clean' : (b ? b.toLowerCase() : null)),
      clockNote: b.replace(/^(dirty|clean)\s*/i, '').trim() || null,
      departing: pax(row[2]),
      inHouse: pax(dTxt) ? { ...pax(dTxt), arrived: inM ? inM[1] : null, arrivedKey: inM ? dateKey(inM[1]) : null, night: inM ? +inM[2] : null, nights: inM ? +inM[3] : null } : null,
      expected: pax(eTxt) ? { ...pax(eTxt), eta: eta ? to24(eta[1], eta[2]) : null } : null,
      comments: [],
      day,
    };
    rooms.push(current);
  }

  // Comments belong to the in-house booking. Late checkout applies to whoever is
  // leaving; extras only to a guest who is staying on (an arrival's extras are
  // never in this report and are entered by reception from the folio).
  for (const r of rooms) {
    r.job = deriveJob(r);
    const info = r.comments.map(readComment);
    r.lateCheckout = info.map((i) => i.lateCheckout).find(Boolean) || null;
    r.extrasFromClock = r.job === 'stay' ? [...new Set(info.flatMap((i) => i.extras))] : [];
    r.flags = [];
    delete r.day;
    if (/room changed to/i.test(r.clockNote || '')) r.flags.push(r.clockNote);
  }

  const messages = [];
  if (!header) messages.push('This does not look like the Clock Housekeeping report (no "Room | Sector" header).');
  if (!rooms.length) messages.push('No rooms found in the file.');
  if (totals && totals.rooms) {
    const dep = rooms.filter((r) => r.departing).length;
    const inh = rooms.filter((r) => r.inHouse).length;
    const exp = rooms.filter((r) => r.expected).length;
    if (dep !== totals.rooms.departures) messages.push(`Departures: read ${dep}, Clock total says ${totals.rooms.departures}.`);
    if (inh !== totals.rooms.inHotel) messages.push(`In hotel: read ${inh}, Clock total says ${totals.rooms.inHotel}.`);
    if (exp !== totals.rooms.expected) messages.push(`Expected: read ${exp}, Clock total says ${totals.rooms.expected}.`);
  }
  return { rooms, totals, check: { ok: messages.length === 0, messages } };
}

/* ───────────────────────── Rooms Charges report ─────────────────────────
 * Clock: Reports → Rooms charges → Excel. Columns A Room ("203 Twin", merged
 * down over the room's bookings), B "Booking #33779, 27 Oct 2026 - 28 Oct 2026",
 * C "Breakfast resident, 2.0 x 25.00 EUR". Extras (robes, chocolates…) are
 * charges on the booking, so the arriving booking's charges are its extras.
 */
// Clock extras at Errigal (confirmed by Karthika, 26 Oct 2026): 1HR R&R SPA
// EXPERIENCE, R&R SPA, BEAUTY TREATMENT, BREAKFAST IN BED, BUBBLY &
// CHOCOLATES, CELEBRATION BALLOONS, FLOWERS, LATE CHECK-OUT, LAUNDRY, PACKED
// LUNCH, ROOM SERVICE CHARGE, TICKETS. Robes are not sold: every spa guest
// gets robes in the room. Late check-out is read separately (departing guest).
const EXTRA_RULES = [
  [/r\s*&\s*r\s*spa/i, 'Robes (spa guest)'], // R&R spa only – no robes for Riverside spa / beauty treatments
  [/robe/i, 'robes'],
  [/bubbly|prosecco|champagne|sparkling/i, 'Bubbly & chocolates'],
  [/choc/i, 'Chocolates'],
  [/flower|bouquet|roses?\b|petal/i, 'Flowers'],
  [/balloon/i, 'Balloons'],
  [/cake/i, 'Cake'],
  [/strawberr/i, 'Strawberries'],
  [/\bcot\b/i, 'Cot'],
];

export function chargeToExtra(name, qty) {
  for (const [re, label] of EXTRA_RULES) {
    if (re.test(name)) {
      if (label === 'Robes (spa guest)' || label === 'Bubbly & chocolates') return label;
      if (label === 'robes') return `${qty && qty > 1 ? Math.round(qty) : 2} robes`;
      return qty && qty > 1 ? `${label} ×${Math.round(qty)}` : label;
    }
  }
  return null;
}

export function isChargesReport(rows) {
  const h = (rows || []).find((r) => r && r.some((c) => s(c)));
  return !!h && /^room$/i.test(s(h[0])) && /booking/i.test(s(h[1])) && /charge/i.test(s(h[2]));
}

/** → { bookings: [{ room, type, booking, from, to, charges: [{ name, qty, text }] }] } */
export function parseChargesReport(rows) {
  const bookings = [];
  let room = null, type = null, cur = null;
  for (const row of rows || []) {
    if (!row) continue;
    const a = s(row[0]), b = s(row[1]), c = s(row[2]);
    if (/^room$/i.test(a)) continue;
    const rm = a.match(/^(\d{3,4})\s*(.*)$/);
    if (rm) { room = rm[1]; type = rm[2].trim() || null; }
    const bm = b.match(/Booking\s*#\s*(\d+)\s*,\s*(\d{1,2} \w{3} \d{4})\s*-\s*(\d{1,2} \w{3} \d{4})/i);
    if (bm && room) {
      cur = { room, type, booking: bm[1], from: dateKey(bm[2]), to: dateKey(bm[3]), charges: [] };
      bookings.push(cur);
    }
    if (c && cur) {
      const target = cur;
      // Several charges in one cell are run together with no separator:
      // "Breakfast resident, 2.0 x 25.00 EURR&R Spa, 2.0 x 30.00 EUR" — split
      // after each "qty x price CUR".
      c.replace(/(\d+(?:[.,]\d+)?\s*x\s*[\d.,]+\s*[A-Z]{3})(?=\S)/g, '$1\n')
        .split(/\r?\n|;\s*/).map((x) => x.trim()).filter(Boolean).forEach((line) => {
        const cm = line.match(/^(.*?),\s*([\d.]+)\s*x\s*[\d.,]+\s*\w*$/i);
        target.charges.push({ name: cm ? cm[1].trim() : line, qty: cm ? parseFloat(cm[2]) : 1, text: line });
      });
    }
  }
  return { bookings };
}

/** Extras per room for guests ARRIVING on `day` (yyyy-mm-dd). */
export function extrasForArrivals(parsed, day) {
  const out = {};
  for (const bk of parsed.bookings) {
    if (bk.from !== day) continue;
    const extras = bk.charges.map((ch) => chargeToExtra(ch.name, ch.qty)).filter(Boolean);
    const raw = bk.charges.map((ch) => ch.text);
    out[bk.room] = { extras: [...new Set(extras)], raw, type: bk.type };
  }
  return out;
}

/** Rooms whose guest LEAVING on `day` has a LATE CHECK-OUT charge → { room: true }. */
export function lateCheckoutsForDepartures(parsed, day) {
  const out = {};
  for (const bk of parsed.bookings) {
    if (bk.to === day && bk.charges.some((ch) => /late\s*check\s*-?\s*out/i.test(ch.name))) out[bk.room] = true;
  }
  return out;
}
